// Auth context.
//
// Session state is derived from the backend: on mount we always call /me to
// check whether the httpOnly session cookie is valid. There is no client-side
// token or localStorage entry — the browser attaches the cookie automatically
// via `credentials: 'include'` on every API request.
//
// Loading starts as true and becomes false once the initial /me check resolves
// (either with agent info or with a 401 that sets agent=null).

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { login as apiLogin, logout as apiLogout, getMe, type AgentInfo } from '../api/auth';
import { ApiError, isAuthFailure } from '../api/client';
import { unregisterCurrentToken } from '../push/native';

interface AuthState {
  agent: AgentInfo | null;
  loading: boolean;
  /** The session could not be checked (network error, 429, 5xx/503). This is NOT "signed out": the guard shows a
   *  retry screen and the check is retried; only an authoritative 401 sends the user to the login page. */
  sessionUnavailable: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  /** Revokes the session on the server and clears this device's per-account state. Resolves false (and keeps the
   *  user signed in) when the server could not confirm the logout, e.g. offline. */
  logout: () => Promise<boolean>;
  /** Re-fetch /me — used after a self-service password change so mustChangePassword
   *  flips to false and AuthGuard stops rendering ForcedPasswordChange, without
   *  requiring a fresh login (the existing session cookie is still valid). */
  refreshAgent: () => Promise<void>;
  /** Re-run the session check now (the retry screen's button). */
  retrySession: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Backoff between automatic session re-checks while the server is unreachable (ms). */
export const SESSION_RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 30_000, 60_000];

/** Per-account state this app keeps in browser storage; cleared on logout so the next account never sees it. */
const ACCOUNT_STORAGE_KEYS = ['outbound_company_name'];

/** An authoritative "no valid session": 401, or the signed-in agent no longer exists (404 on /me). */
function isSignedOut(err: unknown): boolean {
  return isAuthFailure(err) || (err instanceof ApiError && err.status === 404);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    agent: null,
    loading: true, // always start loading — check /me before rendering guards
    sessionUnavailable: false,
  });
  const attempt = useRef(0);
  const timer = useRef<number | null>(null);

  const checkSession = useCallback(() => {
    if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; }
    getMe()
      .then((agent) => {
        attempt.current = 0;
        setState({ agent, loading: false, sessionUnavailable: false });
      })
      .catch((err: unknown) => {
        if (isSignedOut(err)) {
          setState({ agent: null, loading: false, sessionUnavailable: false });
          return;
        }
        const delay = SESSION_RETRY_DELAYS_MS[Math.min(attempt.current, SESSION_RETRY_DELAYS_MS.length - 1)]!;
        attempt.current += 1;
        setState((s) => ({ agent: s.agent, loading: false, sessionUnavailable: s.agent === null }));
        timer.current = window.setTimeout(checkSession, delay);
      });
  }, []);

  useEffect(() => {
    checkSession();
    // Coming back online or to the foreground re-checks immediately instead of waiting for the backoff.
    const again = () => { if (document.visibilityState === 'visible') checkSessionIfWaiting(); };
    const checkSessionIfWaiting = () => { if (timer.current !== null) checkSession(); };
    window.addEventListener('online', checkSessionIfWaiting);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('online', checkSessionIfWaiting);
      document.removeEventListener('visibilitychange', again);
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [checkSession]);

  const login = useCallback(async (email: string, password: string, remember = false) => {
    await apiLogin(email, password, remember);
    const agent = await getMe();
    setState({ agent, loading: false, sessionUnavailable: false });
  }, []);

  const logout = useCallback(async () => {
    await unregisterCurrentToken();
    try {
      await apiLogout();
    } catch (err) {
      // A 401 means the session is already gone — that is a completed logout. Anything else (offline, 5xx): the
      // server did not revoke it, so stay signed in rather than pretend.
      if (!isAuthFailure(err)) return false;
    }
    try { sessionStorage.clear(); } catch { /* storage unavailable */ }
    for (const k of ACCOUNT_STORAGE_KEYS) { try { localStorage.removeItem(k); } catch { /* storage unavailable */ } }
    setState({ agent: null, loading: false, sessionUnavailable: false });
    // A full page load drops every in-memory cache of the previous account before anyone signs in again.
    window.location.replace('/login');
    return true;
  }, []);

  const refreshAgent = useCallback(async () => {
    const agent = await getMe();
    setState({ agent, loading: false, sessionUnavailable: false });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        isAuthenticated: !!state.agent,
        login,
        logout,
        refreshAgent,
        retrySession: checkSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
